import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { companyCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import {
  AddressIcon,
  EmailIcon,
  EoriIcon,
  PersonIcon,
  PhoneIcon,
  PinIcon,
} from 'components/Shared/Icons/tooltip_icons';
import { Company } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditCompany from './AddOrEditCompany';
import PersonCard from '../people/PersonCard';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Company;
  showActionButtons: boolean;
  onCompanyDelete?: () => void;
  onCompanyUpdate?: () => void;
}

const CompanyCard = (props: Props) => {
  const { option: company, showActionButtons = true, onCompanyDelete, onCompanyUpdate } = props;

  const [editCompanyModalOpen, setEditCompanyModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteCompany = () => {
    axiosService
      .delete(`/entities/company/${company.id}/`)
      .then(() => {
        if (onCompanyDelete) {
          onCompanyDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onCompanyEdit = () => {
    if (onCompanyUpdate) {
      onCompanyUpdate();
    }
    setEditCompanyModalOpen(false);
  };

  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  return (
    <>
      <Card elevation={1} sx={companyCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={companyCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditCompanyModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={companyCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={company.name} tooltipText={company.id} />}
          subheader="Company"
          sx={companyCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={companyCardStyles.contactBox}>
            <EmailIcon />
            <Typography color="text" sx={companyCardStyles.contactValue}>
              {company.email || '-'}
            </Typography>
          </Box>
          <Box sx={companyCardStyles.contactBox}>
            <PhoneIcon />
            <Typography color="text" sx={companyCardStyles.contactValue}>
              {company.phone || '-'}
            </Typography>
          </Box>
          <Box sx={companyCardStyles.contactBox}>
            <AddressIcon />
            <Typography color="text" sx={companyCardStyles.contactValue}>
              {company.address || '-'}
            </Typography>
          </Box>
          <Box sx={companyCardStyles.contactBox}>
            <PinIcon />
            <Typography color="text" sx={companyCardStyles.contactValue}>
              {company.eik_bulstat || '-'}
            </Typography>
          </Box>
          <Box sx={companyCardStyles.contactBox}>
            <EoriIcon />
            <Typography color="text" sx={companyCardStyles.contactValue}>
              {company.eori || '-'}
            </Typography>
          </Box>
          <Box sx={companyCardStyles.contactBox}>
            <PersonIcon tooltipText="Contact Person" />
            <Tooltip
              title={
                company.contact_person ? <PersonCard option={company.contact_person} showActionButtons={false} /> : null
              }
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'transparent',
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <Typography color="text" sx={companyCardStyles.contactValue}>
                {company.contact_person
                  ? `${company.contact_person.first_name} ${company.contact_person.last_name}`
                  : '-'}
              </Typography>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete company</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete company <b>{company.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteCompany} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditCompany
        company={company}
        onSave={onCompanyEdit}
        modalTitle="Edit company"
        isModalOpen={editCompanyModalOpen}
        setModalOpen={setEditCompanyModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default CompanyCard;
